import { useCallback, useRef, useState } from 'react';
import { ListBoxData } from 'components/ListBox';
import { useDelay } from 'hooks';
import { SelectProps } from './types';

/**
 * When the select menu doesn't have enough space to render, there is a chance that
 * it will cover its target element.  If the user clicks to open the select menu and
 * the menu covers the target, then when the user mouses up, a selection will be made
 * and the menu will be closed.  This makes the select menu unusable when it covers its
 * reference.
 *
 * This hook solves that problem by setting a cooldown for opening and closing the menu.
 * When the menu opens, the cooldown is triggered and the menu cannot be closed nor
 * a selection made until the cooldown expires.  This is closer to the browser's
 * native implementation than react-aria's defaults.
 *
 * @see https://github.com/adobe/react-spectrum/issues/4422#issuecomment-1521627900
 */
export default function useSelectStateManagement<T extends ListBoxData>(
  props: SelectProps<T>,
) {
  const {
    defaultOpen,
    defaultSelectedKey,
    isOpen: isOpenProp,
    onOpenChange,
    onSelectionChange,
    selectedKey: selectedKeyProp,
  } = props;

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [selectedKey, setSelectedKey] = useState(defaultSelectedKey);
  const isCoolingDown = useRef(false);

  const isIsOpenControlled = isOpenProp !== undefined;
  const isSelectedKeyControlled = selectedKeyProp !== undefined;

  const [resetCooldownAfterDelay] = useDelay(
    useCallback(() => {
      isCoolingDown.current = false;
    }, []),
    200,
  );

  const coolDown = useCallback(() => {
    isCoolingDown.current = true;
    resetCooldownAfterDelay();
  }, [resetCooldownAfterDelay]);

  const handleOpenChange = useCallback(
    (nextIsOpen: boolean) => {
      if (isCoolingDown.current) {
        return;
      }

      if (nextIsOpen) {
        coolDown();
      }

      if (!isIsOpenControlled) {
        setIsOpen(nextIsOpen);
      }

      onOpenChange?.(nextIsOpen);
    },
    [coolDown, isIsOpenControlled, onOpenChange],
  );

  const handleSelectionChange = useCallback(
    (value: React.Key) => {
      if (isCoolingDown.current) {
        return;
      }

      if (!isSelectedKeyControlled) {
        setSelectedKey(value);
      }

      onSelectionChange?.(value);
    },
    [isSelectedKeyControlled, onSelectionChange],
  );

  return {
    isOpen: isIsOpenControlled ? isOpenProp : isOpen,
    onOpenChange: handleOpenChange,
    onSelectionChange: handleSelectionChange,
    selectedKey: isSelectedKeyControlled ? selectedKeyProp : selectedKey,
  };
}
